/**
 * Swipe compare — spec §F5.10 ("swipe (vertical 4px brand handle, drag; thematic panes clip
 * right of handle; mutually exclusive with AOI clip — activating either pauses the other
 * with toast)") and §G6 ("Swipe sets `inset(0 0 0 {x}px)` on the same panes").
 *
 * Geometry, the `⇔` knob and the `ew-resize` cursor are the mockup's `#swipeHandle`. Two
 * things the mockup did not have:
 *
 * * the handle is a real `<button>` on an ARIA slider role and moves with ←/→ (and
 *   Home/End), because a pointer-only control fails §I5;
 * * the drag is pointer-capture based, so releasing outside the window still ends it.
 *
 * The x is deliberately **not** in the §F1 store: it is a transient pixel position in
 * container space, it changes every animation frame while dragging, and nothing outside
 * this component (permalink included) has any use for it. `swipe` — the on/off that §G6
 * makes exclusive with `aoiClip` — is the store's.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppStore } from '../../lib/state';
import { setPaneClip } from './AoiClip';
import { useMap } from './mapContext';

/** Keeps the handle (and therefore a sliver of each side) reachable at any map width. */
export const SWIPE_EDGE_PX = 40;
/** Keyboard step for ←/→ — a comfortable nudge, not a pixel crawl. */
const KEY_STEP_PX = 24;

const clampX = (x: number, width: number): number =>
  Math.max(SWIPE_EDGE_PX, Math.min(width - SWIPE_EDGE_PX, x));

export function Swipe() {
  const { t } = useTranslation();
  const map = useMap();
  const swipe = useAppStore((s) => s.swipe);
  const [x, setX] = useState<number | null>(null);
  // The applied x, read by the map-move handler without making it a render dependency.
  const xRef = useRef<number | null>(null);
  const draggingRef = useRef(false);

  /** §G6: `inset(0 0 0 {x}px)` on `rasterPane` + `choroPane`. */
  const apply = useCallback(
    (next: number) => {
      const width = map.getSize().x;
      const v = clampX(next, width);
      xRef.current = v;
      setX(v);
      setPaneClip(map, `inset(0 0 0 ${v}px)`);
    },
    [map],
  );

  useEffect(() => {
    if (!swipe) return;
    // Mid-map on activation (the mockup's `setSwipe(map.getSize().x / 2)`), or wherever the
    // reader last left the handle within this session.
    apply(xRef.current ?? map.getSize().x / 2);
    // A pan/zoom/resize does not move the handle, but Leaflet rewrites pane transforms, so
    // the inset is re-asserted — and re-clamped, in case the container just got narrower.
    // `viewreset` alongside the other three: Leaflet fires it — and rewrites every pane
    // transform — on a projection-origin reset, which `move`/`zoom` do not cover. The AOI
    // clip has always listened for it (`AoiClip.ts`), and these two write `clip-path` on
    // the same panes, so a swipe that did not would have its inset silently dropped.
    const reapply = () => apply(xRef.current ?? map.getSize().x / 2);
    map.on('move zoom resize viewreset', reapply);
    return () => {
      map.off('move zoom resize viewreset', reapply);
      setPaneClip(map, '');
    };
  }, [map, swipe, apply]);

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };
  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!draggingRef.current) return;
    apply(e.clientX - map.getContainer().getBoundingClientRect().left);
  };
  const endDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const width = map.getSize().x;
    const at = xRef.current ?? width / 2;
    if (e.key === 'ArrowLeft') apply(at - KEY_STEP_PX);
    else if (e.key === 'ArrowRight') apply(at + KEY_STEP_PX);
    else if (e.key === 'Home') apply(0);
    else if (e.key === 'End') apply(width);
    else return;
    e.preventDefault();
  };

  if (!swipe) return null;

  return (
    <button
      type="button"
      id="swipeHandle"
      role="slider"
      aria-label={t('tools.swipeHandle')}
      aria-orientation="vertical"
      aria-valuemin={0}
      aria-valuemax={Math.round(map.getSize().x)}
      aria-valuenow={Math.round(x ?? map.getSize().x / 2)}
      style={{ left: `${x ?? map.getSize().x / 2}px`, display: 'block' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
    />
  );
}

export default Swipe;
